import { userClient } from "@/lib/connect.client";
import { createLogger } from "@/lib/observability/logger";
import {
  DEFAULT_PREFERENCES,
  GetLocationPrefsParams,
  LocationPreferences,
  STORAGE_KEY,
  UpdateLocationPrefsInput,
  getLocationPrefsQueryKey,
  locationTypeToProto,
  protoToLocationType,
} from "./location-prefs.shared";

/**
 * Get location preferences using client-side transport
 */
export async function getLocationPrefs(
  params: GetLocationPrefsParams = {},
): Promise<LocationPreferences> {
  const logger = createLogger("getLocationPrefs");
  const { userId } = params;

  let postalCode: string | undefined;
  let locationType: "community" | "beyond" | undefined;
  let radiusKm: number | undefined;
  let coordinates: { latitude: number; longitude: number } | undefined;

  if (userId) {
    try {
      const { locationPreferences } = await userClient.getLocationPreferences(
        {},
      );
      postalCode = locationPreferences?.searchPostalCode;
      locationType = locationPreferences?.searchLocationType
        ? protoToLocationType(locationPreferences.searchLocationType)
        : undefined;
      radiusKm = locationPreferences?.searchRadiusKm ?? 30;
      coordinates = locationPreferences?.searchCoordinates;
    } catch (error) {
      // Log error but don't throw - fall back to local storage
      let errMsg =
        "Unknown error occurred while fetching user location preferences";
      if (error instanceof Error) {
        errMsg = error.message;
      }
      const message = `Failed to fetch user location preferences, falling back to local storage: ${errMsg}`;
      logger.logError(message, new Error(errMsg));
      // Continue to local storage fallback below
    }
  }

  // If no data from API (either no user or API failed), use local storage
  if (!postalCode && !locationType && !radiusKm && !coordinates) {
    let storedPrefs = DEFAULT_PREFERENCES;
    try {
      const storedData = window.localStorage.getItem(STORAGE_KEY);
      if (storedData) {
        storedPrefs = JSON.parse(storedData) as LocationPreferences;
      }
    } catch {
      // If JSON parsing fails, use default preferences
      storedPrefs = DEFAULT_PREFERENCES;
    }

    postalCode = storedPrefs.postalCode;
    locationType = storedPrefs.locationType;
    radiusKm = storedPrefs.radiusKm;
    coordinates = storedPrefs.coordinates;
  }

  return {
    locationType: locationType ?? DEFAULT_PREFERENCES.locationType,
    postalCode,
    radiusKm: radiusKm ?? DEFAULT_PREFERENCES.radiusKm,
    coordinates,
  };
}

/**
 * Update location preferences using client-side transport
 * Always saves to local storage, even if the API call fails, so users can continue exploring
 */
export async function updateLocationPrefs(
  newPreferences: UpdateLocationPrefsInput,
  isAuthenticated: boolean,
): Promise<void> {
  // Try to update on server if authenticated, but don't fail if it errors
  if (isAuthenticated) {
    try {
      await userClient.updateLocationPreferences({
        searchLocationType: locationTypeToProto(newPreferences.locationType),
        searchPostalCode:
          newPreferences.locationType === "community"
            ? newPreferences.postalCode
            : undefined,
        searchCoordinates:
          newPreferences.locationType === "community"
            ? newPreferences.coordinates
            : undefined,
        searchRadiusKm:
          newPreferences.locationType === "community"
            ? newPreferences.radiusKm
            : undefined,
      });
    } catch (error) {
      // Log error but continue to save locally so user can explore
      const logger = createLogger("updateLocationPrefs");
      const errMsg = error instanceof Error ? error.message : "Unknown error";
      logger.logError(
        `Failed to update location preferences on server, falling back to local storage: ${errMsg}`,
        error instanceof Error ? error : new Error(errMsg),
      );
    }
  }

  // Always update local storage so user can continue exploring even if API fails
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(newPreferences));

  // Sync to a server-readable cookie so SSR can use location in unauthenticated sessions
  try {
    await fetch("/api/location-prefs", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(newPreferences),
      keepalive: true,
    });
  } catch (err) {
    // Non-blocking
    console.warn("Failed to sync location prefs cookie", err);
  }
}

/**
 * Get React Query options for location preferences on the client
 */
export function getLocationPrefsQueryOptions(
  params: GetLocationPrefsParams = {},
) {
  const { userId } = params;

  return {
    queryKey: getLocationPrefsQueryKey(userId),
    queryFn: () => getLocationPrefs(params),
    // Retry once, then stop retrying to prevent infinite loops
    retry: 1,
    // Don't retry on mount if the query previously failed
    retryOnMount: false,
    // Don't refetch on mount (already global, but being explicit)
    refetchOnMount: false,
  };
}
